import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { analyticsAPI } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { LabelWithInfo } from '../../components/InfoTooltip';
import { managementHelp } from '../../content/managementHelp';

const BUCKETS = [
  { value: '', label: 'All buckets' },
  { value: '0_3', label: '0–3 days' },
  { value: '4_7', label: '4–7 days' },
  { value: '8_15', label: '8–15 days' },
  { value: '15_plus', label: '15+ days' },
];

const statusLabel = (s, sub) => {
  const map = {
    demo_completed: 'Demo completed',
    wip_after_demo: 'Follow-up after demo',
    enrollment_pending: 'Enrollment pending',
    demo_scheduled: 'Demo scheduled',
    demo_reschedule_requested: 'Demo reschedule requested',
    wip_before_demo: 'Before demo',
    new: 'New',
  };
  const main = map[s] || s;
  return sub ? `${main} — ${sub}` : main;
};

const StuckLeadsList = ({ backPath = '/management' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hideSalesColumn = user?.role === 'sales';
  const [searchParams] = useSearchParams();
  const [bucket, setBucket] = useState(searchParams.get('bucket') || '');

  const { data: res, isLoading, error } = useQuery(
    ['stuckLeads', bucket],
    () => analyticsAPI.getStuckLeads({ bucket: bucket || undefined }),
    { staleTime: 30000 }
  );

  const leads = res?.data?.data?.leads || [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LabelWithInfo
          label={
            <Typography variant="h5" component="h1">
              Stuck after demo
            </Typography>
          }
          title={managementHelp.ageing.section.title}
          body={managementHelp.ageing.section.body}
        />
      </Box>

      <FormControl size="small" sx={{ mb: 2, minWidth: 160 }}>
        <InputLabel>Bucket</InputLabel>
        <Select label="Bucket" value={bucket} onChange={(e) => setBucket(e.target.value)}>
          {BUCKETS.map((b) => (
            <MenuItem key={b.value || 'all'} value={b.value}>
              {b.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error.message}</Alert>}

      {!isLoading && !error && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                {!hideSalesColumn && <TableCell>Sales</TableCell>}
                <TableCell>Demo date</TableCell>
                <TableCell>Days waiting</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={hideSalesColumn ? 5 : 6} align="center">
                    No stuck leads in this bucket.
                  </TableCell>
                </TableRow>
              )}
              {leads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.display_id}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {lead.display_id}
                    </Typography>
                  </TableCell>
                  {!hideSalesColumn && <TableCell>{lead.sales_person_name}</TableCell>}
                  <TableCell>{lead.demo_date}</TableCell>
                  <TableCell>{lead.days_waiting}</TableCell>
                  <TableCell>{statusLabel(lead.status, lead.sub_status)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => navigate(`/leads?search=${encodeURIComponent(lead.display_id || '')}`)}
                    >
                      Open lead
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default StuckLeadsList;
